// TravelScope.js
import React from 'react';
import { motion } from 'framer-motion';
import { Route, FileCheck, Award, CheckCircle, MessageSquare, TrendingUp, AlertCircle } from 'lucide-react';

const TravelScope = () => {
  const services = [
    {
      id: 1,
      icon: Route,
      title: "Route & requirements guidance",
      description: "Understand options (visit, business, study, work, family), typical requirements, and realistic timelines."
    },
    {
      id: 2,
      icon: FileCheck,
      title: "Document checklists & templates",
      description: "Tailored list by route/destination; templates for sponsor/employer/invitation letters; file-naming and formatting rules."
    },
    {
      id: 3,
      icon: Award,
      title: "Background certificates",
      description: "Police clearance guidance; employment/education confirmations via our Verification team (consent-based); bank reference letters and income/asset proof (where available)."
    },
    {
      id: 4,
      icon: CheckCircle,
      title: "Application organization & QA",
      description: "Arrange documents in embassy-friendly order with cross-references; final admin quality check before you submit."
    },
    {
      id: 5,
      icon: MessageSquare,
      title: "Appointment & interview prep",
      description: "Support with scheduling biometrics/interviews (where applicable) and mock interviews with feedback."
    },
    {
      id: 6,
      icon: TrendingUp,
      title: "Post-decision admin support",
      description: "If refused, we provide a gap analysis and documentation plan for a future attempt. If approved, we share a practical next-steps checklist."
    }
  ];

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="text-left mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack">
            What we do 
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                className="text-left"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {/* Icon */}
                <div className="flex items-start mb-6">
                  <div className="w-16 h-16 bg-acYellow rounded-full flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-inter font-bold text-acBlack mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Important Note */}
        <motion.div
          className="bg-yellow-50 border-l-4 border-acYellow p-6 rounded-r-lg"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-acYellow flex-shrink-0 mr-3 mt-0.5" />
            <div>
              <h4 className="font-semibold text-acBlack mb-2">Important</h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                We do not provide legal advice or representation, make decisions for embassies, or influence outcomes. Submission is done by you (or a visa center); we guide the process.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TravelScope;
